import { Box, styled } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';

import {
  AircraftIcon,
  EAircraftStatus,
  EFlightCategory,
  EMaoMaintenanceBadge,
  ENOPOrganType,
  type ESeverity,
} from '#shared';

import { useData } from '../../data-context';
import {
  getAircraftStatus,
  getAircraftTimeSeverity,
  getAirportLabel,
} from '../../helpers';

import { useAircraftDrawer } from './AircraftDrawerContext';

const PREFIX = 'TSMED-AircraftDrawerLocation';

export const aircraftDrawerLocationClasses = {
  root: `${PREFIX}-root`,
  captions: `${PREFIX}-captions`,
  delimiter: `${PREFIX}-delimiter`,
  caption: `${PREFIX}-caption`,
  airports: `${PREFIX}-airports`,
  airport: `${PREFIX}-airport`,
  airportIcon: `${PREFIX}-airportIcon`,
  airportIATA: `${PREFIX}-airportIATA`,
  airportTime: `${PREFIX}-airportTime`,
};

const Root = styled(Box)(({ theme: { spacing, palette, mixins, utils } }) => ({
  [`&.${aircraftDrawerLocationClasses.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
    background: 'none',
    paddingBlock: spacing(2),
  },
  [`.${aircraftDrawerLocationClasses.captions}`]: {
    color: palette.grey[700],
    display: 'flex',
    fontSize: utils.clamp(14, 20),
  },
  [`.${aircraftDrawerLocationClasses.delimiter}`]: {
    flexGrow: 1,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  [`.${aircraftDrawerLocationClasses.caption}`]: {},
  [`&.${aircraftDrawerLocationClasses.root} .${aircraftDrawerLocationClasses.airports}`]:
    {
      display: 'flex',
      fontSize: utils.clamp(14, 24),
      background: 'none',
    },
  [`.${aircraftDrawerLocationClasses.airport}`]: {
    // color: palette.grey[700],
    display: 'flex',
    gap: spacing(1),
    padding: spacing(0.25, 1),
    alignItems: 'center',
    cursor: 'pointer',
    color: `var(--flightCategoryColor)`,
    background: `var(--flightCategorySecondaryColor) `,
  },
  [`.${aircraftDrawerLocationClasses.airportIcon}`]: {
    height: utils.clamp(16, 32),
    width: utils.clamp(16, 32),
    margin: spacing(-0.5),
    fill: 'var(--organColor, var(--aircraftStatusColor, var(--severityStatusColor, white)))',
    background: 'none',
  },
  [`.${aircraftDrawerLocationClasses.airportIATA}`]: {},
  [`.${aircraftDrawerLocationClasses.airportTime}`]: {},
  ...mixins.organStyles(palette),
  ...mixins.aircraftStatusStyles(palette),
  ...mixins.flightCategoryStyles(palette, { secondaryOpacity: 0.2 }),
  color: 'var(--primaryColor)',
  background: 'var(--secondaryColor)',
  [`.aircraft-status-${EAircraftStatus.Maintenance}`]: {
    color: palette.error[700],
    [`&.maintenance-type-${EMaoMaintenanceBadge.AOG}`]: {
      color: palette.error[200],
    },
  },
}));

interface ActiveAirport {
  id: number;
  caption: string;
  iata: string | null;
  icao: string | null;
  label: string;
  time: string;
  organ: ENOPOrganType | null;
  maintenanceType?: EMaoMaintenanceBadge;
  flightCategory?: EFlightCategory;
  timeSeverity?: ESeverity | null;
  isOnFlight: boolean;
  IanaTimeZone: string;
}

export const AircraftDrawerLocation = () => {
  const { aircraftData, selectAirport, selectedAirport } = useAircraftDrawer();
  const {
    actualOrgan,
    maintenanceType,
    tripLegActual: tripLegActual,
    isOnFlight,
    airportLastKnown,
  } = aircraftData;
  const actualTimeSeverity = getAircraftTimeSeverity(aircraftData);
  const actualStatus = getAircraftStatus(aircraftData);
  const { metarsData } = useData();
  useEffect(() => {
    selectAirport(
      airportLastKnown
        ? {
            Id: airportLastKnown.Id,
            ICAO: airportLastKnown.ICAO,
            IanaTimeZone: airportLastKnown.IanaTimeZone,
          }
        : undefined,
    );
    return () => {
      selectAirport();
    };
  }, [airportLastKnown, selectAirport]);
  const { airport1, airport2 } = useMemo((): {
    airport1?: ActiveAirport;
    airport2?: ActiveAirport;
  } => {
    if (!tripLegActual) {
      const airport = airportLastKnown;
      if (!airport) {
        return {};
      }
      const { ICAO, IATA, IanaTimeZone, Id } = airport;
      const time = dayjs().tz(IanaTimeZone).format('HH:mm z');
      const flightCategory =
        (ICAO && metarsData[ICAO]?.flightCategory) || EFlightCategory.NA;

      return {
        airport1: {
          id: Id,
          caption: 'Now at',
          iata: IATA,
          icao: ICAO,
          label: getAirportLabel(IATA, ICAO),
          time,
          flightCategory,
          maintenanceType: maintenanceType?.OpsBoardBadge,
          organ: actualOrgan,
          timeSeverity: actualTimeSeverity,
          isOnFlight: false,
          IanaTimeZone,
        },
      };
    }
    const { DepartureAirport, ArrivalAirport } = tripLegActual;
    const departureTimezone = DepartureAirport.IanaTimeZone;
    const arrivalTimezone = ArrivalAirport.IanaTimeZone;
    const ArrivalICAO = ArrivalAirport.ICAO;
    const ArrivalIATA = ArrivalAirport.IATA;
    const DepartureICAO = DepartureAirport.ICAO;
    const DepartureIATA = DepartureAirport.IATA;
    const DepartureFlightCategory =
      (DepartureICAO && metarsData[DepartureICAO]?.flightCategory) ||
      EFlightCategory.NA;
    const ArrivalFlightCategory =
      (ArrivalICAO && metarsData[ArrivalICAO]?.flightCategory) ||
      EFlightCategory.NA;

    return {
      airport1: {
        id: DepartureAirport.Id,
        caption: isOnFlight
          ? `Departed (${dayjs(tripLegActual.OffBlocksUtc).tz(departureTimezone).fromNow()})`
          : 'Now at',
        iata: DepartureIATA,
        icao: DepartureICAO,
        label: getAirportLabel(DepartureIATA, DepartureICAO),
        time: dayjs(tripLegActual.OffBlocksUtc)
          .tz(departureTimezone)
          .format('HH:mm z'),
        flightCategory: DepartureFlightCategory,
        organ: actualOrgan,
        timeSeverity: actualTimeSeverity,
        isOnFlight,
        IanaTimeZone: tripLegActual.DepartureAirport.IanaTimeZone,
      },
      airport2: {
        id: ArrivalAirport.Id,
        caption: isOnFlight
          ? `Arrive (${dayjs().tz(arrivalTimezone).to(dayjs(tripLegActual.OnBlocksUtc).tz(arrivalTimezone))})`
          : 'Next',
        iata: ArrivalIATA,
        icao: ArrivalICAO,
        label: getAirportLabel(ArrivalIATA, ArrivalICAO),
        time: dayjs(tripLegActual.OnBlocksUtc)
          .tz(arrivalTimezone)
          .format('HH:mm z'),
        flightCategory: ArrivalFlightCategory,
        organ: actualOrgan,
        timeSeverity: actualTimeSeverity,
        isOnFlight,
        IanaTimeZone: tripLegActual.ArrivalAirport.IanaTimeZone,
      },
    };
  }, [
    tripLegActual,
    isOnFlight,
    metarsData,
    actualOrgan,
    actualTimeSeverity,
    airportLastKnown,
    maintenanceType?.OpsBoardBadge,
  ]);

  useEffect(() => {
    if (!selectedAirport && airport2) {
      // selectAirport(arrivalAirport.icao);
    }
  }, [airport2, selectAirport, selectedAirport]);
  return airport1 ? (
    <Root className={classNames(aircraftDrawerLocationClasses.root)}>
      <Box className={aircraftDrawerLocationClasses.captions}>
        <Box className={aircraftDrawerLocationClasses.caption}>
          {airport1.caption}
        </Box>
        <Box className={aircraftDrawerLocationClasses.delimiter} />
        {airport2 ? (
          <Box className={aircraftDrawerLocationClasses.caption}>
            {airport2.caption}
          </Box>
        ) : null}
      </Box>
      <Box
        className={classNames(
          aircraftDrawerLocationClasses.airports,
          [`aircraft-status-${actualStatus}`],
          {
            [`severity-status-${actualTimeSeverity}`]: [
              EAircraftStatus.Available,
              EAircraftStatus.OnTrip,
            ].includes(actualStatus),
            [`organ-type-${actualOrgan}`]: actualOrgan,
            [`maintenance-type-${maintenanceType?.OpsBoardBadge}`]:
              !!maintenanceType?.OpsBoardBadge,
          },
        )}
      >
        <Box
          className={classNames(
            aircraftDrawerLocationClasses.airport,
            `flight-category-${airport1.flightCategory}`,
          )}
          onClick={() => {
            selectAirport({
              Id: airport1?.id,
              ICAO: airport1?.icao,
              IanaTimeZone: airport1.IanaTimeZone,
            });
          }}
        >
          {!airport1.isOnFlight ? (
            <AircraftIcon
              className={classNames(aircraftDrawerLocationClasses.airportIcon, {
                [`organ-type-${actualOrgan}`]: actualOrgan,
              })}
            />
          ) : null}
          <Box className={aircraftDrawerLocationClasses.airportIATA}>
            {airport1.label}
          </Box>
          {actualStatus === EAircraftStatus.OnTrip ? (
            <Box className={aircraftDrawerLocationClasses.airportTime}>
              {airport1.time}
            </Box>
          ) : null}
        </Box>
        <Box className={aircraftDrawerLocationClasses.delimiter}>
          {airport1.isOnFlight ? (
            <AircraftIcon
              className={classNames(aircraftDrawerLocationClasses.airportIcon, {
                [`organ-type-${actualOrgan}`]: actualOrgan,
              })}
            />
          ) : null}
        </Box>
        {airport2 ? (
          <Box
            className={classNames(
              aircraftDrawerLocationClasses.airport,
              `flight-category-${airport2.flightCategory}`,
            )}
            onClick={() => {
              selectAirport({
                Id: airport2?.id,
                ICAO: airport2?.icao,
                IanaTimeZone: airport2.IanaTimeZone,
              });
            }}
          >
            <Box className={aircraftDrawerLocationClasses.airportIATA}>
              {airport2.label}
            </Box>
            <Box className={aircraftDrawerLocationClasses.airportTime}>
              {airport2.time}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Root>
  ) : null;
};
